import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, DialogContentType, DialogFooterType, IconNames } from '@dotcom/crown-ui';
import { getCurrentRegion } from '../../regional-config/get-regional-config';
import { ConfirmationDialogProps, DeleteConfirmationDialogProps } from './confirmation-dialog-types';

export const ConfirmationDialog = ({
  domId,
  title,
  actionButtonText,
  cancelButtonText,
  contentText,
  isOpen,
  onClose,
  onConfirm,
}: ConfirmationDialogProps): React.ReactElement => {
  const { t } = useTranslation();
  const region = getCurrentRegion();

  const actionButton = (
    <Button onClick={() => onConfirm()} variant="primary" testId={'confirmation-dialog-action-button'}>
      {actionButtonText}
    </Button>
  );

  const cancelButton = (
    <Button variant="secondary" onClick={() => onClose()} testId={'confirmation-dialog-cancel-button'}>
      {cancelButtonText ? cancelButtonText : t('confirmationDialog.cancelButton')}
    </Button>
  );

  return (
    <Dialog
      id={domId}
      testId={domId}
      accentIcon={IconNames.CardFeaturesBold}
      hasGrayBackground={false}
      type={DialogContentType.Modal}
      isOpen={isOpen}
      onClose={onClose}
      actionButton={actionButton}
      cancelButton={cancelButton}
      title={title}
      footerType={DialogFooterType.Cancel}
      closeButtonId={`${domId}-close-button`}
      locale={region}
    >
      {contentText || ''}
    </Dialog>
  );
};

export const DeleteConfirmationDialog = ({
  isOpen,
  onClose,
  onConfirm,
}: DeleteConfirmationDialogProps): React.ReactElement => {
  return (
    <ConfirmationDialog
      domId="delete-confirmation-dialog"
      title="Are you sure you want to delete this?"
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={onConfirm}
      actionButtonText="Delete"
    />
  );
};

export const ResetConfirmationDialog = ({
  isOpen,
  onClose,
  onConfirm,
}: DeleteConfirmationDialogProps): React.ReactElement => {
  const contentText = "If this image isn't already saved in your photo library, you'll lose it permanently.";

  return (
    <ConfirmationDialog
      domId="reset-confirmation-dialog"
      title="Reset this image?"
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={onConfirm}
      contentText={contentText}
      actionButtonText="Reset"
    />
  );
};

export const ResetTextConfirmationDialog = ({
  isOpen,
  onClose,
  onConfirm,
}: DeleteConfirmationDialogProps): React.ReactElement => {
  const { t } = useTranslation();

  const contentText = t('resetTextDialogPhotoTextZone.content');

  return (
    <ConfirmationDialog
      domId="reset-text-confirmation-dialog"
      title={t('resetTextDialogPhotoTextZone.title')}
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={onConfirm}
      contentText={contentText}
      actionButtonText={t('resetTextDialogPhotoTextZone.actionButton')}
    />
  );
};

export const ClearConfirmationDialog = ({
  isOpen,
  onClose,
  onConfirm,
}: DeleteConfirmationDialogProps): React.ReactElement => {
  return (
    <ConfirmationDialog
      domId="clear-confirmation-dialog"
      title="Are you sure you want to clear this content?"
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={onConfirm}
      actionButtonText="Clear"
    />
  );
};

export const DeleteQuickAddressConfirmationDialog = ({
  isOpen,
  onClose,
  onConfirm,
}: DeleteConfirmationDialogProps): React.ReactElement => {
  return (
    <ConfirmationDialog
      domId="delete-quick-address-confirmation-dialog"
      title="Delete address?"
      contentText="This will permanently remove this address from your Address Book"
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={onConfirm}
      actionButtonText="Delete"
      cancelButtonText="Cancel"
    />
  );
};

export const DeleteAddressFromProjectConfirmationDialog = ({
  isOpen,
  onClose,
  onConfirm,
}: DeleteConfirmationDialogProps): React.ReactElement => {
  return (
    <ConfirmationDialog
      domId="delete-address-from-project-confirmation-dialog"
      title="Delete address?"
      contentText="This will remove this address from your current project. Are you sure?"
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={onConfirm}
      actionButtonText="Delete"
      cancelButtonText="Cancel"
    />
  );
};

export const RemoveFileDialog = ({ isOpen, onClose, onConfirm }: DeleteConfirmationDialogProps): React.ReactElement => {
  return (
    <ConfirmationDialog
      domId="remove-file-confirmation-dialog"
      title="Remove file?"
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={onConfirm}
      contentText="This cannot be undone."
      actionButtonText="Delete"
      cancelButtonText="Cancel"
    />
  );
};
